import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog'
import { recoverRequestsApi } from '@/features/recover-requests/api'
import { getStatusPillClass } from '@/features/recover-requests/components/RecoveryTrackingHeader'
import { Notebook, Copy, Check } from 'lucide-react'
import { useState, useCallback } from 'react'
import { useToast } from '@/hooks/use-toast'

function formatDateShort(date: Date): string {
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })
}

function stripHtml(html: string): string {
    if (!html) return ''
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent?.trim() ?? ''
}

interface ViewUpdatesDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    requestID: number | null
    objectID: string
    objectDescription: string | null
}

export function ViewUpdatesDialog({
    open,
    onOpenChange,
    requestID,
    objectID,
    objectDescription,
}: ViewUpdatesDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const [copiedId, setCopiedId] = useState<string | null>(null)

    const { data: assignmentsData, isLoading } = useQuery({
        queryKey: ['recovery-assignments', requestID],
        queryFn: async () => {
            if (!requestID) return { assignments: [], updateTimeRemainingMinutes: null }
            const response = await recoverRequestsApi.getRecoveryAssignments(requestID)
            const payload = response.data?.data
            if (!payload) return { assignments: [], updateTimeRemainingMinutes: null }
            if (Array.isArray(payload)) return { assignments: payload, updateTimeRemainingMinutes: null }
            return { assignments: payload.assignments ?? [], updateTimeRemainingMinutes: payload.updateTimeRemainingMinutes ?? null }
        },
        enabled: open && requestID != null && requestID > 0,
    })

    const updates = useMemo(() => {
        if (!assignmentsData?.assignments) return []
        return assignmentsData.assignments.map((a) => ({
            id: a.id || '',
            action: a.action || '',
            dateTime: a.actiondatetime ? new Date(a.actiondatetime) : new Date(),
            username: a.userName || (a.userID != null ? String(a.userID) : ''),
            message: a.update || '',
        }))
    }, [assignmentsData])

    const copyUpdate = useCallback(async (message: string, id: string) => {
        const text = stripHtml(message) || message
        await navigator.clipboard.writeText(text)
        setCopiedId(id)
        setTimeout(() => setCopiedId(null), 2000)
        toast({ title: t('common.copied') || 'Copied' })
    }, [t, toast])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Notebook className="h-5 w-5" />
                        {t('updateRunBook.recoveryUpdates', 'Recovery Updates')}
                    </DialogTitle>
                    <DialogDescription>
                        {objectID}{objectDescription ? ` — ${objectDescription}` : ''}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-auto min-h-0">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-muted-foreground">{t('common.loading', 'Loading...')}</div>
                        </div>
                    ) : updates.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-muted-foreground">{t('recoverRequests.noUpdatesYet', 'No updates yet')}</div>
                        </div>
                    ) : (
                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/50">
                                        <th className="text-left px-4 py-2 font-medium text-muted-foreground w-10">{t('recoverRequests.status', 'Status')}</th>
                                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">{t('recoverRequests.action', 'Action')}</th>
                                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">{t('recoverRequests.dateTime', 'Date & time')}</th>
                                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">{t('recoverRequests.user', 'User')}</th>
                                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">{t('recoverRequests.engineerUpdate', 'Engineer update')}</th>
                                        <th className="w-10"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {updates.map((u) => (
                                        <tr key={u.id} className="border-b last:border-b-0 hover:bg-muted/30">
                                            <td className="px-4 py-2">
                                                <div className={`w-3 h-3 rounded-full ${
                                                    u.action === 'Completed' ? 'bg-green-500' :
                                                    u.action === 'Started' ? 'bg-blue-500' :
                                                    u.action === 'InProgress' ? 'bg-amber-500' :
                                                    u.action === 'Issue' ? 'bg-red-500' :
                                                    u.action === 'Assigned' ? 'bg-green-500' :
                                                    'bg-gray-400'
                                                }`} />
                                            </td>
                                            <td className="px-4 py-2">
                                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusPillClass(u.action)}`}>
                                                    {u.action}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                                                {formatDateShort(u.dateTime)}
                                            </td>
                                            <td className="px-4 py-2">
                                                {u.username || '—'}
                                            </td>
                                            <td className="px-4 py-2">
                                                {u.message ? (
                                                    <div
                                                        className="prose prose-sm max-w-none [&_p]:mb-0.5 [&_ul]:list-disc [&_ol]:list-decimal line-clamp-2 text-foreground"
                                                        dangerouslySetInnerHTML={{ __html: u.message }}
                                                    />
                                                ) : (
                                                    <span className="text-muted-foreground">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2">
                                                {u.message && (
                                                    <button
                                                        type="button"
                                                        onClick={() => copyUpdate(u.message, u.id)}
                                                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                                        title={t('common.copy')}
                                                    >
                                                        {copiedId === u.id ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
